<script setup>
  import { onMounted } from 'vue'
  import IpSearchWidget from "@/components/Query/IpSearchWidget.vue"
  import SearchHistory from "@/components/Account/SearchHistory.vue"
  import ManageEnrichments from "@/components/SlideOver/views/ManageEnrichments.vue"
  import { useMainStore } from '@/stores/main'
  import { useDataSourcesStore } from '@/stores/dataSources'
  const mainStore = useMainStore()
  const dataSourcesStore = useDataSourcesStore()

  onMounted(() => {
    dataSourcesStore.init()
  })

  function openManageEnrichments() {
    mainStore.show_slideover(ManageEnrichments, {}, { width: 40 }, 'Enrichments')
  }
</script>

<template>
  <div class="container pt-5">
    <div class="heading-container">
      <div class="d-flex centered column">
        <h1 class="text-center jura mb-4">
          Enrich IP Addresses and Networks
        </h1>
        <p style="max-width: 900px;" class="text-center mb-3">
          Bulk-enrich IP addresses and networks with vital context to quickly identify threats, anomalies, and key pivot points.
        </p>
      </div>
    </div>

    <div class="toolbar-sticky">
      <div class="toolbar">
        <button
          class="type-btn type-ip active"
          data-guide="enrich-ip-tab"
        >
          <font-awesome-icon :icon="['fas', 'network-wired']" />
          IP Addresses
        </button>
      </div>
    </div>

    <div class="search-widget-wrapper">
      <IpSearchWidget style="height: 400px;" />
    </div>

    <div class="enrichment-badges-section">
      <div class="enrichment-badges-header">
        <div class="enrichment-badges-label">Enabled Enrichments</div>
        <button class="enrichment-manage-btn" @click="openManageEnrichments">
          Manage
          <font-awesome-icon icon="arrow-right" class="ms-1" />
        </button>
      </div>
      <div class="enrichment-badges-container">
        <div class="enrichment-badges-track">
          <div
            v-for="e in dataSourcesStore.enrichments"
            :key="e.label"
            class="enrichment-badge"
            :class="{ disabled: !e.active }"
            :style="{ borderLeftColor: e.accent }"
          >
            <div class="enrichment-badge-title">
              <div class="enrichment-badge-icon" :style="{ color: e.accent }">
                <img v-if="e.providerLogo" src="~@/assets/ws_logo_idea.svg" class="enrichment-badge-logo" />
                <font-awesome-icon v-else :icon="['fas', e.icon]" />
              </div>
              <span class="enrichment-badge-label">{{ e.label }}</span>
              <font-awesome-icon v-if="e.active" :icon="['fas', 'circle-check']" class="enrichment-badge-check" />
            </div>
            <span class="enrichment-badge-desc">{{ e.desc }}</span>
          </div>
        </div>
        <div class="enrichment-badges-fade"></div>
      </div>
    </div>

    <div class="history-wrapper">
      <SearchHistory filterType="ip"/>
    </div>
  </div>
</template>

<style scoped>
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.heading-container {
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  padding-top: 1rem;
}

.toolbar-sticky {
  flex-shrink: 0;
  margin-bottom: 1.25rem;
}

.toolbar {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #151a22;
  border-radius: 1rem;
  gap: 0.5rem;
}

.type-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  color: #7A8696;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: default;
  font-family: inherit;
  white-space: nowrap;
}

.type-btn.type-ip.active {
  background: rgba(115, 87, 210, 0.15);
  color: #a78bfa;
}

.search-widget-wrapper {
  flex-shrink: 0;
}

.enrichment-badges-section {
  flex-shrink: 0;
  padding: 1.5rem 0 2rem;
}

.enrichment-badges-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.enrichment-badges-label {
  color: #9CA3AF;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.enrichment-manage-btn {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #6B7280;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.enrichment-manage-btn:hover {
  background-color: rgba(255, 255, 255, 0.05);
  color: #E5E7EB;
}

.enrichment-badges-container {
  position: relative;
  display: flex;
  overflow: hidden;
}

.enrichment-badges-fade {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 160px;
  background: linear-gradient(to right, transparent, var(--bg-color, #191f27) 70%);
  pointer-events: none;
}

.enrichment-badges-track {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

.enrichment-badge {
  display: flex;
  flex-direction: column;
  padding: 1rem 1.5rem;
  background: rgba(26, 29, 36, 0.6);
  border-radius: 0.5rem;
  border-left: 3px solid;
  color: #e2e8f0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.enrichment-badge.disabled {
  opacity: 0.35;
  border-left-style: dashed;
}

.enrichment-badge-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.enrichment-badge-icon {
  width: 1rem;
  height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
  line-height: 1;
}

.enrichment-badge-logo {
  height: 1rem;
  width: auto;
  opacity: 0.8;
}

.enrichment-badge-label {
  font-weight: 500;
  font-size: 1rem;
  white-space: nowrap;
  line-height: 1;
}

.enrichment-badge-check {
  font-size: 0.7rem;
  color: #34d399;
  opacity: 0.7;
  flex-shrink: 0;
}

.enrichment-badge-desc {
  font-size: 0.8rem;
  color: #7A8696;
  white-space: nowrap;
  line-height: 1;
  margin-top: 0.5rem;
}

.history-wrapper {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

</style>
